// The route from the app to Coding (or Updates): a top or bottom row with a
// fold arrow and a gear, a folded row's tab, or the floating gear with its
// menu (Android's FeatureAccess, AccessRow, AccessBlip, FloatingGear).
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Glyph } from '../../ui/glyphs.jsx';
import { destination, gearPlace, gearOffset, gearFraction, pastSlop } from '../../lib/access.js';

const GEAR = 56;

/**
 * A shortcut row. `edge` is 'top' | 'bottom'. onSettings is null when the
 * style is kept by the creator. install (platform.js installEntry) adds an
 * Install button that calls onInstall; the row has room for one short word.
 */
export function AccessRow({ edge, edition, onOpen, onSettings, onFold, install, onInstall, children, settingsLabel = 'App settings' }) {
  const dest = destination(edition);
  return (
    <div class={`mt-access-row mt-at-${edge}`} role="navigation" aria-label={`${dest} shortcut`}>
      <div class="mt-access-row-inner">
        <button type="button" class="mt-btn mt-text mt-access-dest" onClick={onOpen}>{dest}</button>
        <span class="mt-spacer" />
        {children}
        {install && (
          <button type="button" class="mt-btn mt-text mt-access-dest mt-access-install" onClick={onInstall}>
            <Glyph name="install" />Install
          </button>
        )}
        <button type="button" class="mt-icon-btn" aria-label="Fold the shortcut away" onClick={onFold}>
          {/* The arrow points at the edge the row folds into. */}
          <Glyph name="expand" class={edge === 'top' ? 'mt-rot180' : ''} />
        </button>
        {onSettings && (
          <button type="button" class="mt-icon-btn" aria-label={settingsLabel} onClick={onSettings}><Glyph name="gear" /></button>
        )}
      </div>
    </div>
  );
}

/** A folded row: a small tab on its edge that brings the row back. */
export function AccessTab({ edge, edition, onExpand }) {
  return (
    <button type="button" class={`mt-access-tab mt-at-${edge}`} aria-label={`Show the ${destination(edition)} shortcut`} onClick={onExpand}>
      <span><Glyph name="expand" class={edge === 'top' ? '' : 'mt-rot180'} /></span>
    </button>
  );
}

/**
 * The floating gear. Follows a drag past touch slop and stays where it is
 * released (onMoved(fractions)); a tap opens its menu, or the destination
 * directly when onSettings is null (a kept style). Arrow keys move it too.
 * install adds an Install item to the menu (a kept style has no menu, so
 * no item: Coding → Settings still has Add to home screen).
 */
export function FloatingGear({ insetTop = 0, insetBottom = 0, settings, edition, onOpen, onSettings, onMoved, onTouch, install, onInstall, settingsLabel = 'App settings' }) {
  const dest = destination(edition);
  const room = useRef(null);
  const btn = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [drag, setDrag] = useState(null); // {left, top} while moving
  const [menu, setMenu] = useState(false);
  const gesture = useRef(null);
  const suppress = useRef(false); // the click that ends a drag is not a tap

  useLayoutEffect(() => {
    const el = room.current;
    const measure = () => setSize({ width: Math.max(0, el.clientWidth - GEAR), height: Math.max(0, el.clientHeight - GEAR) });
    measure();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, []);
  useEffect(() => { setDrag(null); }, [settings.x, settings.y, settings.corner]);
  useEffect(() => {
    if (!menu) return undefined;
    const rootNode = room.current.getRootNode();
    const close = (e) => { if (!room.current?.querySelector('.mt-gear-menu')?.contains(e.target) && e.target !== btn.current) setMenu(false); };
    rootNode.addEventListener('pointerdown', close, true);
    room.current.querySelector('.mt-gear-menu button')?.focus();
    return () => rootNode.removeEventListener('pointerdown', close, true);
  }, [menu]);

  const place = gearPlace(settings);
  const at = drag ?? gearOffset(place, size);

  function activate() {
    onTouch?.();
    if (!onSettings) onOpen(); else setMenu((m) => !m);
  }
  function down(e) {
    if (e.button != null && e.button !== 0) return;
    gesture.current = { id: e.pointerId, x: e.clientX, y: e.clientY, from: at, moving: false };
    try { btn.current.setPointerCapture(e.pointerId); } catch { /* old browser */ }
  }
  function move(e) {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.moving && !pastSlop(dx, dy)) return;
    g.moving = true;
    setMenu(false);
    setDrag({ left: Math.min(size.width, Math.max(0, g.from.left + dx)), top: Math.min(size.height, Math.max(0, g.from.top + dy)) });
  }
  function up(e) {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    gesture.current = null;
    if (g.moving) {
      suppress.current = true;
      setTimeout(() => { suppress.current = false; }, 400);
      onTouch?.();
      const dx = e.clientX - g.x;
      const dy = e.clientY - g.y;
      const left = Math.min(size.width, Math.max(0, g.from.left + dx));
      const top = Math.min(size.height, Math.max(0, g.from.top + dy));
      setDrag({ left, top });
      onMoved(gearFraction(left, top, size));
    }
  }
  function click() {
    if (suppress.current) { suppress.current = false; return; }
    activate();
  }
  function key(e) {
    const step = 0.05;
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) { if (e.key === 'Escape') setMenu(false); return; }
    e.preventDefault();
    const clamp = (v) => Math.min(1, Math.max(0, Math.round(v * 100) / 100));
    onMoved({ x: clamp(place.x + d[0]), y: clamp(place.y + d[1]) });
  }
  const moving = gesture.current?.moving === true;

  // The menu opens towards the middle of the screen.
  const below = place.y < 0.5;
  const alignLeft = place.x < 0.5;
  const menuStyle = `${below ? `top:${at.top + GEAR + 8}px` : `bottom:${size.height - at.top + 8}px`};${alignLeft ? `left:${at.left}px` : `right:${size.width - at.left}px`}`;
  return (
    <div class="mt-gear-room mt-shell-pass" ref={room} style={{ top: insetTop > 0 ? `${insetTop + 12}px` : undefined, bottom: insetBottom > 0 ? `${insetBottom + 12}px` : undefined }}>
      <button type="button" ref={btn} class={`mt-gear${moving ? ' mt-dragging' : ''}`} style={`left:${at.left}px;top:${at.top}px`}
        aria-label={onSettings ? `${dest} and ${settingsLabel.toLowerCase()}` : dest} aria-haspopup={onSettings ? 'menu' : undefined}
        aria-expanded={onSettings ? (menu ? 'true' : 'false') : undefined}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={() => { gesture.current = null; setDrag(null); }}
        onClick={click} onKeyDown={key}>
        <Glyph name="gear" />
      </button>
      {menu && onSettings && (
        <div class="mt-gear-menu" role="menu" style={menuStyle} onKeyDown={(e) => {
          const items = [...e.currentTarget.querySelectorAll('button')];
          const i = items.indexOf(e.currentTarget.getRootNode().activeElement);
          if (e.key === 'Escape') { setMenu(false); btn.current?.focus(); }
          else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); items[(i + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length]?.focus(); }
        }}>
          <button type="button" role="menuitem" onClick={() => { setMenu(false); onOpen(); }}><Glyph name="sparkle" />{dest}</button>
          <button type="button" role="menuitem" onClick={() => { setMenu(false); onSettings(); }}><Glyph name="gear" />{settingsLabel}</button>
          {install && <button type="button" role="menuitem" onClick={() => { setMenu(false); onInstall(); }}><Glyph name="install" />{install.label}</button>}
        </div>
      )}
    </div>
  );
}
